import Tooltip from "rc-tooltip";
import React, { Fragment, useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getDestinationByCode } from "shared/data/actions/destinations";
import { AppState } from "shared/data/reducers";
import {
  isCarryOnBaggageItem,
  isCarryOnSpiBaggageItem,
} from "shared/lib/baggageUtils";
import { getTextWidth, makeAbsoluteUrl } from "shared/lib/functions";
import {
  BaggageItem,
  SegmentBaggage,
} from "shared/lib/golObjectTypes/BaggageDetails/BaggageDetails";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";

import useMediaQuery from "../../../hooks/useMediaQuery";
import { Column } from "./index";

type Destination = {
  $t: string;
  Code: string;
  Country: string;
  Parent: string;
  State: string;
};
interface Props {
  data: SegmentBaggage;
  unit: "KILOGRAMS" | "POUNDS";
  intl: IntlShape;
  columns: Column[];
}

type BaggageRenderData = string | [React.ReactNode, React.ReactNode];

const WithTooltip = ({ width, text, label }) => {
  const textString = typeof text === "string" ? text : "";
  const isTextNarrow = textString
    ? getTextWidth(textString, "0.8rem Muli") < width - 16
    : false;
  return (
    <Tooltip
      mouseEnterDelay={0.5}
      mouseLeaveDelay={0.4}
      overlay={typeof text === "string" ? <span>{text}</span> : text}
      overlayClassName="reservation-table-tooltip"
      overlayStyle={isTextNarrow ? { display: "none" } : {}}
    >
      <div
        className="flight-additional-info-baggage-modal-table-row-cell"
        style={{ width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          {text}
        </div>
      </div>
    </Tooltip>
  );
};

const BaggageModalRow = ({ data, intl, unit, columns }: Props) => {
  const reachedDesktop = useMediaQuery(1010);
  const [originName, setOriginName] = useState();
  const [destinationName, setDestinationName] = useState();
  const dispatch = useDispatch();

  const airportDestinations: Destination[] = useSelector(
    (state: AppState) => state.storage.airportDestinations
  );

  useEffect(() => {
    const findAirportDestinationByCode = async (code) => {
      if (!code) {
        return null;
      }
      return getDestinationByCode(code, airportDestinations)(dispatch);
    };

    (async () => {
      setOriginName(await findAirportDestinationByCode(data.Origin));
      setDestinationName(await findAirportDestinationByCode(data.Destination));
    })();
    // eslint-disable-next-line
  }, [data]);

  const isIncluded = (dataLocal: BaggageItem) => {
    return dataLocal.Price === "0";
  };

  const getIncludedIcon = (dataLocal: BaggageItem): React.ReactElement => {
    const isIncludedValue = isIncluded(dataLocal);
    return (
      <Img
        src={
          isIncludedValue
            ? "/static/images/cbt-round-checkmark.svg"
            : "/static/images/cross-red.svg"
        }
        alt={isIncludedValue ? "Included" : "Not included"}
        className="flight-additional-info-baggage-modal-baggage-icon"
      />
    );
  };

  const getWeight = (dataLocal: BaggageItem) => {
    if (dataLocal.WeightKG === "0") {
      return "";
    }

    return unit === "KILOGRAMS"
      ? `${dataLocal.WeightKG} kg`
      : `${dataLocal.WeightLB} lbs`;
  };

  const getDimensions = (dataLocal: BaggageItem) => {
    if (dataLocal.DimensionCM === "0") {
      return "";
    }
    return unit === "KILOGRAMS"
      ? [`${dataLocal.DimensionCM} cm`, <sup key="DimensionCM">*</sup>]
      : [`${dataLocal.DimensionIN} in`, <sup key="DimensionIN">*</sup>];
  };

  const getQuantity = (dataLocal: BaggageItem) => {
    if (dataLocal.Quantity === "0") {
      return "";
    }

    const quantity = `${dataLocal.Quantity} ${intl.formatMessage({
      id: "Units.pieces",
    })}`;

    return isCarryOnSpiBaggageItem(dataLocal)
      ? `${quantity} ${intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.smallUnderseatBag",
        })}`
      : quantity;
  };

  const getLink = (dataLocal: SegmentBaggage) => (
    <a
      className="link"
      target="_blank"
      rel="noopener noreferrer"
      href={makeAbsoluteUrl(dataLocal.Url.$t)}
    >
      <FormattedMessage id="FlightAdditionalInfoBaggage.learnMore" />
    </a>
  );

  if (airportDestinations.length === 0) {
    return null;
  }

  let firstBaggage: BaggageRenderData = "-";
  const carryOn: BaggageRenderData[] = [];
  let baggageData: BaggageRenderData = "-";

  data.BaggageItem.forEach((baggage: BaggageItem, idx) => {
    const isEmpty = (it: string) => it === "0" || !it;
    const priceIsZero = isEmpty(baggage.Price);
    const quantityIsEmpty = isEmpty(baggage.Quantity);
    const weightIsEmpty = isEmpty(baggage.WeightKG);
    const dimensionIsEmpty = isEmpty(baggage.DimensionCM);

    const allFieldsEmpty = quantityIsEmpty && weightIsEmpty && dimensionIsEmpty;

    if (priceIsZero && allFieldsEmpty) {
      baggageData = [
        <div
          key={idx}
          className="flight-additional-info-baggage-modal-baggage-info__icon"
        >
          <Img
            src="/static/images/cross-red.svg"
            alt="Not included"
            className="flight-additional-info-baggage-modal-baggage-icon"
          />
        </div>,
        <FormattedMessage
          key={`not-allowed-${idx}`}
          id="FlightAdditionalInfoBaggage.notAllowed"
        />,
      ];
    } else {
      const isIncludedValue = priceIsZero;

      const quantityStr = getQuantity(baggage);
      const weightStr = getWeight(baggage);
      const dimensionValue = getDimensions(baggage);

      const contentParts = [];
      if (quantityStr) contentParts.push(quantityStr);
      if (weightStr) contentParts.push(weightStr);

      const contentText =
        contentParts.length > 0 ? `${contentParts.join(" ")} ` : "";

      baggageData = [
        <div
          key={idx}
          className="flight-additional-info-baggage-modal-baggage-info__icon"
        >
          {getIncludedIcon(baggage)}
        </div>,
        isIncludedValue ? (
          [contentText, dimensionValue]
        ) : (
          <FormattedMessage
            key={`not-allowed-${idx}`}
            id="FlightAdditionalInfoBaggage.notAllowed"
          />
        ),
      ];
    }

    switch (baggage.Type) {
      case "first":
        firstBaggage = baggageData;
        break;
      case "carry-on":
        carryOn.push(baggageData);
        break;
      default:
        if (isCarryOnBaggageItem(baggage)) {
          carryOn.push(baggageData);
        }
        return null;
    }
  });

  if (carryOn.length === 0) {
    carryOn.push("-");
  }

  // @ts-ignore
  const originNameLabel = originName ? originName.$t : "";
  // @ts-ignore
  const destinationNameLabel = destinationName ? destinationName.$t : "";

  const renderBaggageData = (baggage: BaggageRenderData) =>
    typeof baggage === "string" ? (
      <span>{baggage}</span>
    ) : (
      <div className="flight-additional-info-baggage-modal-baggage-info">
        {baggage[0]}
        <div className="flight-additional-info-baggage-modal-baggage-info__meta">
          {baggage[1]}
        </div>
      </div>
    );

  return (
    <div className="flight-additional-info-baggage-modal-table-row ">
      {!reachedDesktop ? (
        <div className="flight-additional-info-baggage-modal-table-row-header">
          <div className="flight-additional-info-baggage-modal-table-row-header-item">
            <img alt="icon" src="/static/images/ico-plane-departure.svg" />
            {`${originNameLabel} (${data.Origin})`}
          </div>
          <div className="flight-additional-info-baggage-modal-table-row-header-item">
            <img
              alt="icon"
              className="reservations-table-row-arrival-icon"
              src="/static/images/ico-plane-arrivals.svg"
            />
            {`${destinationNameLabel} (${data.Destination})`}
          </div>
        </div>
      ) : (
        <WithTooltip
          width={columns[0].width}
          text={
            <div>
              <div>{`${originNameLabel} (${data.Origin})`}</div>
              <div>{`${destinationNameLabel} (${data.Destination})`}</div>
            </div>
          }
          label={columns[0].label}
        />
      )}

      <div
        className="flight-additional-info-baggage-modal-table-row-cell"
        style={{ width: columns[1].width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[1].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          <Tooltip
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.4}
            overlay={
              <span>
                <FormattedMessage id="FlightAdditionalInfoBaggage.dimensionDescription" />
              </span>
            }
            overlayClassName="reservation-table-tooltip-dimension-description"
          >
            {typeof firstBaggage === "string" ? (
              <span>{firstBaggage}</span>
            ) : (
              renderBaggageData(firstBaggage)
            )}
          </Tooltip>
        </div>
      </div>

      <div
        className="flight-additional-info-baggage-modal-table-row-cell "
        style={{ width: columns[2].width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[2].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          {carryOn.map((baggage, index) => {
            const lastItem = carryOn.length - 1 === index;
            return (
              <Fragment key={index}>
                {renderBaggageData(baggage)}
                {!lastItem && <br key={index} />}
              </Fragment>
            );
          })}
        </div>
      </div>

      <div className="flight-additional-info-baggage-modal-table-row-cell">
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[3].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          {getLink(data)}
        </div>
      </div>
    </div>
  );
};

export default BaggageModalRow;
